import mapboxgl from 'mapbox-gl';

const {
    accessToken = null,
    mapboxStyleUsername = null,
    mapboxStyleId = null,
    mapboxLocationImg = null,
} = window.MapboxConfig;

const mapContainer = document.getElementById('map');

mapboxgl.accessToken = accessToken;

const initializeMap = mapContainer => {
    const enablePopup = window.mapEnablePopup ?? true;
    const offers = JSON.parse(window.map);
    const offersFlat = Object.values(offers).reduce((array, offers) => array.concat(offers), []);
    const bounds = offersFlat.reduce(
        (boundsObject, offer) => boundsObject.extend([offer.longitude, offer.latitude]),
        new mapboxgl.LngLatBounds(),
    );

    const map = new mapboxgl.Map({
        container: mapContainer,
        center: [offersFlat[0].longitude, offersFlat[0].latitude],
        zoom: 12,
        bounds: offersFlat.length > 1 ? bounds : false,
        fitBoundsOptions:
            offersFlat.length > 1 ? { padding: window.innerWidth > 600 ? 100 : 40 } : false,
        style: `mapbox://styles/${mapboxStyleUsername}/${mapboxStyleId}`,
        cooperativeGestures: true,
    });

    map.on('load', async () => {
        const geojson = {
            type: 'FeatureCollection',
            features: Object.entries(offers).map(([latLngKey, offers]) => ({
                type: 'Feature',
                properties: {
                    offerHtml: offers
                        .map(
                            offer =>
                                `<article class="map-popup__entry">
                                    <div class="text-wrapper">
                                        <a class="map-popup__link" href="${offer.link}">
                                            <h3 class="map-popup__headline">${offer.headline}</h3>
                                        </a>
                                        <p class="map-popup__address mb-0">${offer.address}</p>
                                    </div>
                                    <div class="map-popup__icon-wrapper" aria-hidden="true">
                                        <svg class="map-popup__icon" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 320 512"><!--! Font Awesome Pro 6.1.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"></path></svg>
                                    </div>
                                </article>`,
                        )
                        .join(''),
                },
                geometry: {
                    type: 'Point',
                    coordinates: latLngKey.split(',').reverse(),
                },
            })),
        };

        map.addControl(new mapboxgl.NavigationControl());

        map.addControl(
            new mapboxgl.GeolocateControl({
                positionOptions: {
                    enableHighAccuracy: true,
                },
                trackUserLocation: true,
                showUserHeading: true,
            }),
        );

        map.addSource('offers', {
            type: 'geojson',
            data: geojson,
            cluster: true,
            clusterRadius: 50,
        });

        map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'offers',
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': '#d61c23',
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff',
                'circle-radius': ['step', ['get', 'point_count'], 20, 20, 30, 100, 40],
            },
        });

        map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'offers',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-font': ['Open Sans SemiBold', 'Arial Unicode MS Regular'],
                'text-size': 18,
            },
            paint: {
                'text-color': '#fff',
            },
        });

        map.loadImage(mapboxLocationImg, function (error, image) {
            if (error) {
                map.addLayer({
                    id: 'marker',
                    type: 'circle',
                    source: 'offers',
                    filter: ['!', ['has', 'point_count']],
                    paint: {
                        'circle-color': '#d61c23',
                        'circle-radius': 20,
                        'circle-stroke-width': 2,
                        'circle-stroke-color': '#fff',
                    },
                });
                throw error;
            }

            map.addImage('custom-marker', image);
            map.addLayer({
                id: 'marker',
                type: 'symbol',
                source: 'offers',
                filter: ['!', ['has', 'point_count']],
                layout: {
                    'icon-image': 'custom-marker',
                    'icon-size': 0.5,
                },
            });
        });

        const showPopupForMarker = marker => {
            const coordinates = marker.geometry.coordinates.slice();
            const offerHtml = marker.properties.offerHtml;

            if (enablePopup) {
                new mapboxgl.Popup({ focusAfterOpen: false })
                    .setLngLat(coordinates)
                    .setMaxWidth('none')
                    .setOffset([0, -30])
                    .setHTML(
                        `<div class="map__popup map-popup">
                        <div class="map-popup__content">
                            ${offerHtml}
                        </div>
                    </div>`,
                    )
                    .addTo(map);
            }
        };

        map.on('click', 'marker', e => showPopupForMarker(e.features[0]));

        if (offers.length == 1) {
            showPopupForMarker(geojson.features[0]);
        }

        map.on('click', 'clusters', e => {
            const features = map.queryRenderedFeatures(e.point, {
                layers: ['clusters'],
            });
            const clusterId = features[0].properties.cluster_id;
            map.getSource('offers').getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return;
                map.easeTo({
                    center: features[0].geometry.coordinates,
                    zoom: zoom,
                });
            });
        });

        map.on('mouseenter', 'clusters', () => {
            map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', 'clusters', () => {
            map.getCanvas().style.cursor = '';
        });

        map.on('mouseenter', 'marker', () => {
            map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', 'marker', () => {
            map.getCanvas().style.cursor = '';
        });
    });
};

const consentManager = window.klaro.getManager();

if (consentManager.getConsent('mapbox')) {
    initializeMap(mapContainer);
}

consentManager.watch({
    update(obj, name, data) {
        if (name !== 'applyConsents') return;
        if (!obj.states.mapbox) return;
        initializeMap(mapContainer);
    },
});
